import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, legacyAppUrl } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { PERFIL_LABELS } from '@/lib/rbac/matrix'
import type { Perfil } from '@/lib/rbac/types'
import {
  Alert,
  DataTable,
  Empty,
  Kpi,
  KpiGrid,
  Muted,
  PageHeader,
  Section,
  StatusBadge,
} from '@/components/ui'
import {
  createUsuario,
  deleteUsuario,
  fetchUsuarios,
  toggleUsuarioAtivo,
  updateUsuario,
} from './api'
import type { UsuarioAdmin, UsuarioWritePayload } from './types'

const PERFIL_OPTS: Array<{ id: string; label: string }> = [
  { id: 'admin', label: 'Administrador' },
  { id: 'rh', label: 'Gestor de RH' },
  { id: 'analista', label: 'Analista de RH' },
  { id: 'gestor', label: 'Gestor de Equipe' },
  { id: 'colab', label: 'Colaborador' },
  { id: 'juridico', label: 'Jurídico' },
]

const EMPTY_FORM: UsuarioWritePayload & { senha2?: string } = {
  nome: '',
  email: '',
  perfil: 'colab',
  senha: '',
  senha2: '',
  is_active: true,
  trocar_senha: true,
}

function perfilMeta(perfil: string) {
  const key = (perfil === 'colaborador' ? 'colab' : perfil) as Perfil
  return PERFIL_LABELS[key] || { label: perfil, cor: '#64748b' }
}

function formatDate(iso?: string | null) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('pt-BR')
}

export function UsuariosPage() {
  const { user, canEdit } = useAuth()
  const qc = useQueryClient()
  const editable = canEdit('usuarios')

  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')
  const [perfil, setPerfil] = useState('todos')
  const [ativo, setAtivo] = useState('')
  const [page, setPage] = useState(1)
  const [msg, setMsg] = useState('')
  const [msgTone, setMsgTone] = useState<'info' | 'danger'>('info')
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<UsuarioAdmin | null>(null)
  const [form, setForm] = useState({ ...EMPTY_FORM })

  const lista = useQuery({
    queryKey: ['admin', 'usuarios', q, perfil, ativo, page],
    queryFn: () =>
      fetchUsuarios({
        busca: q || undefined,
        perfil: perfil || undefined,
        ativo: ativo || undefined,
        page,
      }),
  })

  const invalidate = async () => {
    await qc.invalidateQueries({ queryKey: ['admin', 'usuarios'] })
  }

  const saveMut = useMutation({
    mutationFn: async () => {
      if (editing) {
        const payload: Partial<UsuarioWritePayload> = {
          nome: form.nome.trim(),
          email: form.email?.trim().toLowerCase(),
          perfil: form.perfil,
          is_active: form.is_active,
          trocar_senha: form.trocar_senha,
        }
        if (form.senha?.trim()) payload.senha = form.senha.trim()
        return updateUsuario(editing.id, payload)
      }
      return createUsuario({
        nome: form.nome.trim(),
        email: form.email?.trim().toLowerCase(),
        perfil: form.perfil,
        senha: form.senha?.trim(),
        is_active: form.is_active ?? true,
        trocar_senha: form.trocar_senha ?? true,
      })
    },
    onSuccess: async () => {
      setMsgTone('info')
      setMsg(editing ? 'Usuário atualizado.' : 'Usuário criado.')
      setShowForm(false)
      setEditing(null)
      setForm({ ...EMPTY_FORM })
      await invalidate()
    },
    onError: (err) => {
      setMsgTone('danger')
      setMsg(err instanceof ApiError ? err.message : 'Falha ao salvar usuário.')
    },
  })

  const toggleMut = useMutation({
    mutationFn: ({ id, next }: { id: string; next: boolean }) => toggleUsuarioAtivo(id, next),
    onSuccess: async () => {
      setMsgTone('info')
      setMsg('Status atualizado.')
      await invalidate()
    },
    onError: (err) => {
      setMsgTone('danger')
      setMsg(err instanceof ApiError ? err.message : 'Falha ao alterar status.')
    },
  })

  const delMut = useMutation({
    mutationFn: (id: string) => deleteUsuario(id),
    onSuccess: async (res) => {
      setMsgTone('info')
      setMsg(
        (res && typeof res === 'object' && 'mensagem' in res && res.mensagem)
          ? String(res.mensagem)
          : 'Usuário removido.',
      )
      await invalidate()
    },
    onError: (err) => {
      setMsgTone('danger')
      setMsg(err instanceof ApiError ? err.message : 'Falha ao excluir.')
    },
  })

  const dados = lista.data?.dados || []
  const meta = lista.data?.meta
  const totais = lista.data?.totais
  const total = meta?.total ?? dados.length
  const porPerfil = totais?.por_perfil || {}
  const meId = user?.id != null ? String(user.id) : ''
  const meEmail = (user?.email || '').toLowerCase()

  function openCreate() {
    setEditing(null)
    setForm({ ...EMPTY_FORM })
    setShowForm(true)
    setMsg('')
  }

  function openEdit(u: UsuarioAdmin) {
    setEditing(u)
    setForm({
      nome: u.nome,
      email: u.email,
      perfil: u.perfil === 'colaborador' ? 'colab' : u.perfil,
      senha: '',
      senha2: '',
      is_active: u.is_active,
      trocar_senha: Boolean(u.trocar_senha),
    })
    setShowForm(true)
    setMsg('')
  }

  function submitForm(e: FormEvent) {
    e.preventDefault()
    if (!form.nome.trim()) {
      setMsg('Informe o nome.')
      return
    }
    if (!editing && !form.email?.trim()) {
      setMsg('Informe o e-mail.')
      return
    }
    if (!editing && (!form.senha || form.senha.length < 6)) {
      setMsg('Senha mínima de 6 caracteres.')
      return
    }
    if (form.senha && form.senha !== form.senha2) {
      setMsg('A confirmação de senha não confere.')
      return
    }
    saveMut.mutate()
  }

  return (
    <section className="hi-page hi-usuarios-page">
      <PageHeader
        title="Usuários"
        subtitle="Contas de acesso ao portal — perfis, status e senha via API accounts."
        actions={
          <>
            <a className="btn btn-secondary" href={legacyAppUrl('#/usuarios')}>
              Legado
            </a>
            {editable ? (
              <button type="button" className="btn btn-primary" onClick={openCreate}>
                Novo usuário
              </button>
            ) : null}
          </>
        }
      />

      {msg ? <Alert tone={msgTone}>{msg}</Alert> : null}

      <KpiGrid>
        <Kpi label="Listados" value={String(total)} />
        <Kpi label="Ativos" value={String(totais?.ativos ?? '—')} />
        <Kpi label="Inativos" value={String(totais?.inativos ?? '—')} />
        {PERFIL_OPTS.slice(0, 3).map((p) => (
          <Kpi key={p.id} label={p.label} value={String(porPerfil[p.id] ?? 0)} />
        ))}
      </KpiGrid>

      {showForm && editable ? (
        <Section title={editing ? `Editar — ${editing.nome}` : 'Novo usuário'}>
          <form className="hi-filters hi-usuarios-form" onSubmit={submitForm}>
            <input
              type="text"
              placeholder="Nome completo"
              value={form.nome}
              onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
              required
              aria-label="Nome"
            />
            <input
              type="email"
              placeholder="E-mail"
              value={form.email || ''}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              required
              aria-label="E-mail"
            />
            <select
              value={form.perfil}
              onChange={(e) => setForm((f) => ({ ...f, perfil: e.target.value }))}
              aria-label="Perfil"
            >
              {PERFIL_OPTS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
            <input
              type="password"
              placeholder={editing ? 'Nova senha (opcional)' : 'Senha'}
              value={form.senha || ''}
              onChange={(e) => setForm((f) => ({ ...f, senha: e.target.value }))}
              minLength={editing ? undefined : 6}
              required={!editing}
              aria-label="Senha"
              autoComplete="new-password"
            />
            <input
              type="password"
              placeholder="Confirmar senha"
              value={form.senha2 || ''}
              onChange={(e) => setForm((f) => ({ ...f, senha2: e.target.value }))}
              required={!editing || Boolean(form.senha)}
              aria-label="Confirmar senha"
              autoComplete="new-password"
            />
            <label className="hi-check">
              <input
                type="checkbox"
                checked={Boolean(form.is_active)}
                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
              />
              Ativo
            </label>
            <label className="hi-check">
              <input
                type="checkbox"
                checked={Boolean(form.trocar_senha)}
                onChange={(e) => setForm((f) => ({ ...f, trocar_senha: e.target.checked }))}
              />
              Trocar senha no próximo login
            </label>
            <button type="submit" className="btn btn-primary" disabled={saveMut.isPending}>
              {saveMut.isPending ? 'Salvando…' : 'Salvar'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setShowForm(false)
                setEditing(null)
              }}
            >
              Cancelar
            </button>
          </form>
        </Section>
      ) : null}

      <section className="card hi-usuarios-panel">
        <div className="card-header">
          <h4 className="card-title">Contas</h4>
          <Muted>{lista.isFetching ? 'Atualizando…' : `${total.toLocaleString('pt-BR')} no filtro`}</Muted>
        </div>

        <form
          className="hi-filters"
          onSubmit={(e) => {
            e.preventDefault()
            setPage(1)
            setQ(search.trim())
          }}
        >
          <input
            type="search"
            placeholder="Buscar nome ou e-mail"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Busca"
          />
          <select
            value={perfil}
            onChange={(e) => {
              setPerfil(e.target.value)
              setPage(1)
            }}
            aria-label="Perfil"
          >
            <option value="todos">Todos os perfis</option>
            {PERFIL_OPTS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
          <select
            value={ativo}
            onChange={(e) => {
              setAtivo(e.target.value)
              setPage(1)
            }}
            aria-label="Status"
          >
            <option value="">Ativos e inativos</option>
            <option value="1">Somente ativos</option>
            <option value="0">Somente inativos</option>
          </select>
          <button type="submit" className="btn btn-secondary">
            Filtrar
          </button>
        </form>

        {lista.isLoading ? <Muted>Carregando…</Muted> : null}
        {lista.isError ? (
          <Alert tone="danger">
            {lista.error instanceof ApiError ? lista.error.message : 'Falha ao listar usuários.'}
          </Alert>
        ) : null}
        {!lista.isLoading && dados.length === 0 ? <Empty>Nenhum usuário neste filtro.</Empty> : null}

        {dados.length > 0 ? (
          <DataTable>
            <thead>
              <tr>
                <th>Usuário</th>
                <th>E-mail</th>
                <th>Perfil</th>
                <th>Setor</th>
                <th>Status</th>
                <th>Criado</th>
                {editable ? <th>Ações</th> : null}
              </tr>
            </thead>
            <tbody>
              {dados.map((u) => {
                const metaP = perfilMeta(u.perfil)
                const isMe =
                  (meId && String(u.id) === meId) ||
                  (meEmail && u.email.toLowerCase() === meEmail)
                return (
                  <tr key={u.id}>
                    <td>
                      <div className="hi-user-cell">
                        <span
                          className="hi-avatar-sm"
                          style={{ background: `${metaP.cor}22`, color: metaP.cor }}
                          aria-hidden
                        >
                          {u.iniciais || u.nome.slice(0, 2).toUpperCase()}
                        </span>
                        <div>
                          <strong>
                            {u.nome}
                            {isMe ? ' · você' : ''}
                          </strong>
                          {u.cargo ? <span className="hi-muted">{u.cargo}</span> : null}
                        </div>
                      </div>
                    </td>
                    <td>
                      <Muted>{u.email}</Muted>
                    </td>
                    <td>
                      <span
                        className="hi-perfil-chip"
                        style={{
                          background: `${metaP.cor}18`,
                          color: metaP.cor,
                          borderColor: `${metaP.cor}40`,
                        }}
                      >
                        {metaP.label}
                      </span>
                    </td>
                    <td>{u.setor || '—'}</td>
                    <td>
                      <StatusBadge tone={u.is_active ? 'ativo' : 'inativo'}>
                        {u.is_active ? 'Ativo' : 'Inativo'}
                      </StatusBadge>
                    </td>
                    <td>
                      <Muted>{formatDate(u.created_at)}</Muted>
                    </td>
                    {editable ? (
                      <td className="hi-actions">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => openEdit(u)}
                        >
                          Editar
                        </button>
                        {!isMe ? (
                          <>
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              disabled={toggleMut.isPending}
                              onClick={() =>
                                toggleMut.mutate({ id: u.id, next: !u.is_active })
                              }
                            >
                              {u.is_active ? 'Desativar' : 'Ativar'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              disabled={delMut.isPending}
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `Excluir permanentemente ${u.nome} (${u.email})?`,
                                  )
                                ) {
                                  delMut.mutate(u.id)
                                }
                              }}
                            >
                              Excluir
                            </button>
                          </>
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                )
              })}
            </tbody>
          </DataTable>
        ) : null}

        {meta && (meta.ultima_pagina || meta.total_paginas || 1) > 1 ? (
          <div className="hi-pager">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Anterior
            </button>
            <Muted>
              Página {meta.pagina ?? page} / {meta.ultima_pagina ?? meta.total_paginas}
            </Muted>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={page >= (meta.ultima_pagina || meta.total_paginas || 1)}
              onClick={() => setPage((p) => p + 1)}
            >
              Próxima
            </button>
          </div>
        ) : null}
      </section>
    </section>
  )
}
